// src/features/chat/hooks/useArtifactFreshness.js
/*
 * Frontier/settle state machine for Zero-effort artifacts.
 *
 * Freshness belongs to the frontier: artifacts that materialize while the
 * user is watching — and approvals that resolve while the user is watching —
 * share one fresh zone and settle together when a newer turn begins (a user
 * send), with an idle backstop timer. Artifacts present at the first loaded
 * snapshot materialize already settled (arrived while away). Settling is
 * display only — undo availability stays server truth.
 *
 * Per-device session state by design: a remount re-initializes everything
 * as settled, which is exactly the arrived-while-away behavior.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { TERMINAL_APPROVAL_STATUSES } from '../models/artifactLifecycleModel';

export const SETTLE_BACKSTOP_MS = 30000;

/**
 * @param {Object} params
 * @param {Array} params.receipts - Effect receipt docs.
 * @param {Array} params.approvals - Approval request docs.
 * @param {boolean} params.artifactsLoaded - Both artifact sources loaded once.
 * @param {string|null} params.lastUserMessageId - Latest user send (incl. optimistic).
 * @param {number} [params.backstopMs]
 * @returns {{
 *   freshIds: Set<string>,
 *   bornIds: Set<string>,
 *   expandedWhisperIds: Set<string>,
 *   toggleWhisper: (id: string) => void,
 * }}
 */
export function useArtifactFreshness({
  receipts = [],
  approvals = [],
  artifactsLoaded = false,
  lastUserMessageId = null,
  backstopMs = SETTLE_BACKSTOP_MS,
}) {
  const [freshIds, setFreshIds] = useState(() => new Set());
  const [bornIds, setBornIds] = useState(() => new Set());
  const [expandedWhisperIds, setExpandedWhisperIds] = useState(() => new Set());
  const knownIdsRef = useRef(new Set());
  const approvalStatusRef = useRef(new Map());
  const initializedRef = useRef(false);
  const backstopTimerRef = useRef(0);
  const lastUserMessageIdRef = useRef(lastUserMessageId);

  const settleAll = useCallback(() => {
    if (backstopTimerRef.current) {
      clearTimeout(backstopTimerRef.current);
      backstopTimerRef.current = 0;
    }
    setFreshIds((current) => (current.size ? new Set() : current));
    setBornIds((current) => (current.size ? new Set() : current));
  }, []);

  // Arrival + resolution tracking: the first snapshot settles; later receipt
  // arrivals and approval resolutions join the shared fresh zone.
  useEffect(() => {
    if (!artifactsLoaded) return;

    const currentIds = [];
    receipts.forEach((receipt) => receipt?.id && currentIds.push(`receipt:${receipt.id}`));
    approvals.forEach((approval) => approval?.id && currentIds.push(`approval:${approval.id}`));

    if (!initializedRef.current) {
      initializedRef.current = true;
      knownIdsRef.current = new Set(currentIds);
      approvals.forEach((approval) => {
        if (approval?.id) {
          approvalStatusRef.current.set(approval.id, approval.status || 'pending');
        }
      });
      return;
    }

    const newIds = currentIds.filter((id) => !knownIdsRef.current.has(id));
    newIds.forEach((id) => knownIdsRef.current.add(id));

    // A watched approval resolving is a witnessed moment too: its whisper
    // stays at the frontier (never folds) until the frontier moves.
    const resolvedNowIds = [];
    approvals.forEach((approval) => {
      if (!approval?.id) return;
      const status = approval.status || 'pending';
      const previous = approvalStatusRef.current.get(approval.id);
      approvalStatusRef.current.set(approval.id, status);
      if (
        previous !== undefined &&
        !TERMINAL_APPROVAL_STATUSES.has(previous) &&
        TERMINAL_APPROVAL_STATUSES.has(status)
      ) {
        resolvedNowIds.push(`approval:${approval.id}`);
      }
    });

    if (newIds.length) {
      setBornIds((current) => {
        const next = new Set(current);
        newIds.forEach((id) => next.add(id));
        return next;
      });
    }

    const newFreshIds = [
      ...newIds.filter((id) => id.startsWith('receipt:')),
      ...resolvedNowIds,
    ];
    if (newFreshIds.length) {
      setFreshIds((current) => {
        const next = new Set(current);
        newFreshIds.forEach((id) => next.add(id));
        return next;
      });
      // Each burst member keeps the shared fresh zone alive.
      if (backstopTimerRef.current) clearTimeout(backstopTimerRef.current);
      backstopTimerRef.current = setTimeout(() => {
        backstopTimerRef.current = 0;
        setFreshIds((current) => (current.size ? new Set() : current));
        setBornIds((current) => (current.size ? new Set() : current));
      }, backstopMs);
    }
  }, [receipts, approvals, artifactsLoaded, backstopMs]);

  // Frontier movement: a newer user send settles everything fresh.
  useEffect(() => {
    if (lastUserMessageIdRef.current === lastUserMessageId) return;
    const isFirstObservation = lastUserMessageIdRef.current === null;
    lastUserMessageIdRef.current = lastUserMessageId;
    if (!isFirstObservation) {
      settleAll();
    }
  }, [lastUserMessageId, settleAll]);

  useEffect(() => () => {
    if (backstopTimerRef.current) clearTimeout(backstopTimerRef.current);
  }, []);

  const toggleWhisper = useCallback((id) => {
    setExpandedWhisperIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return {
    freshIds,
    bornIds,
    expandedWhisperIds,
    toggleWhisper,
  };
}
